import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, mergeMap, of, switchMap } from "rxjs";
import { SnackbarService } from "../../core/services";
import * as OrganizationActions from "./organization.actions";
import { OrganizationApi } from "./organization.api";

@Injectable()
export class OrganizationEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(OrganizationApi);
  private readonly snackbar = inject(SnackbarService);

  organizationList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.OrganizationListRequest),
      switchMap(() =>
        this.api.listOrganizations().pipe(
          map(data => OrganizationActions.OrganizationListSuccess({ organizations: data.data ?? [] })),
          catchError(error => of(OrganizationActions.OrganizationListFailure({ error: error?.error?.message || error?.message || 'Failed to fetch organizations' })))
        )
      )
    )
  );

  organizationCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.OrganizationCreateRequest),
      switchMap(({ payload }) =>
        this.api.createOrganization({
          name: payload.name,
          email: payload.email,
          phone: payload.phone,
          address: {
            address_line_1: payload.address_line_1,
            address_line_2: payload.address_line_2,
            city: payload.city,
            state: payload.state,
            country: payload.country,
            zip: payload.zip,
          },
          is_active: payload.is_active,
        }).pipe(
          map(data => OrganizationActions.OrganizationCreateSuccess({ message: data?.message || 'Organization created successfully' })),
          catchError(error => {
            if (error.status === 422) {
              return of(OrganizationActions.OrganizationValidationError({ validationErrors: error.error.errors }));
            }
            return of(OrganizationActions.OrganizationCreateFailure({ error: error?.error?.message || error?.message || 'An error occurred while creating organization' }));
          })
        )
      )
    )
  );

  organizationGet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.OrganizationGetRequest),
      switchMap(({ id }) =>
        this.api.getOrganization(id).pipe(
          map(data => OrganizationActions.OrganizationGetSuccess({ organization: data.data })),
          catchError(error => {
            this.snackbar.showNotification('error', '', error || 'Failed to fetch organization details', 'Close');
            return of(OrganizationActions.OrganizationGetFailure({ error: error || 'An error occurred while fetching organization details' }));
          })
        )
      )
    )
  );

  organizationGetCurrent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.OrganizationGetCurrentRequest),
      switchMap(() =>
        this.api.getCurrentOrganization().pipe(
          map(data => OrganizationActions.OrganizationGetSuccess({ organization: data.data })),
          catchError(error => {
            this.snackbar.showNotification('error', '', error || 'Failed to fetch organization details', 'Close');
            return of(OrganizationActions.OrganizationGetFailure({ error: error || 'An error occurred while fetching organization details' }));
          })
        )
      )
    )
  );

  organizationUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.OrganizationUpdateRequest),
      switchMap(({ payload }) =>
        this.api.updateOrganization(payload).pipe(
          map(data => OrganizationActions.OrganizationUpdateSuccess({
            id: payload.id,
            message: data?.message || 'Organization updated successfully'
          })),
          catchError(error => {
            if (error.status === 422) {
              return of(OrganizationActions.OrganizationValidationError({ validationErrors: error.error.errors }));
            }
            return of(OrganizationActions.OrganizationUpdateFailure({ error: error || 'An error occurred while updating organization' }));
          })
        )
      )
    )
  );

  organizationUpdateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.OrganizationUpdateSuccess),
      mergeMap(({ id, message }) => {
        this.snackbar.showNotification('success', '', message, 'Close');
        return [
          OrganizationActions.OrganizationGetRequest({ id }),
          OrganizationActions.OrganizationListRequest(),
        ];
      })
    )
  );

  organizationUpdateFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.OrganizationUpdateFailure),
      map(({ error }) => {
        this.snackbar.showNotification('error', '', `Failed to update organization: ${error}`, 'Close');
      })
    ),
    { dispatch: false }
  );

  organizationCreateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.OrganizationCreateSuccess),
      map(({ message }) => {
        this.snackbar.showNotification('success', '', message, 'Close');
        return OrganizationActions.OrganizationListRequest();
      })
    )
  );

  organizationCreateFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.OrganizationCreateFailure),
      map(({ error }) => {
        this.snackbar.showNotification('error', '', `Failed to create organization: ${error}`, 'Close');
      })
    ),
    { dispatch: false }
  );

  organizationListFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.OrganizationListFailure),
      map(({ error }) => {
        this.snackbar.showNotification('error', '', `Failed to fetch organizations: ${error}`, 'Close');
      })
    ),
    { dispatch: false }
  );

  organizationGenerateCredentials$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.OrganizationGenerateCredentialsRequest),
      switchMap(({ id }) =>
        this.api.generateCredentials(id).pipe(
          map(data => OrganizationActions.OrganizationGenerateCredentialsSuccess({
            id,
            appKey: data?.data?.app_key ?? '',
            appSecret: data?.data?.app_secret ?? '',
          })),
          catchError(error => of(OrganizationActions.OrganizationGenerateCredentialsFailure({ error: error?.error?.message || error?.message || 'Failed to generate organization credentials' })))
        )
      )
    )
  );

  organizationGenerateCredentialsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.OrganizationGenerateCredentialsSuccess),
      mergeMap(({ id }) => [
        OrganizationActions.OrganizationGetRequest({ id }),
        OrganizationActions.OrganizationListRequest(),
      ])
    )
  );

  organizationGenerateCredentialsFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.OrganizationGenerateCredentialsFailure),
      map(({ error }) => {
        this.snackbar.showNotification('error', '', `Failed to generate credentials: ${error}`, 'Close');
      })
    ),
    { dispatch: false }
  );

  organizationSetActive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.OrganizationSetActiveRequest),
      switchMap(({ id, isActive }) =>
        this.api.setOrganizationActive(id, isActive).pipe(
          map(data => OrganizationActions.OrganizationSetActiveSuccess({
            id,
            isActive,
            message: data?.message || `Organization ${isActive ? 'enabled' : 'disabled'} successfully`,
          })),
          catchError(error => of(OrganizationActions.OrganizationSetActiveFailure({ error: error?.error?.message || error?.message || 'Failed to update organization status' })))
        )
      )
    )
  );

  organizationSetActiveSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.OrganizationSetActiveSuccess),
      map(({ message }) => {
        this.snackbar.showNotification('success', '', message, 'Close');
        return OrganizationActions.OrganizationListRequest();
      })
    )
  );

  organizationSetActiveFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.OrganizationSetActiveFailure),
      map(({ error }) => {
        this.snackbar.showNotification('error', '', `Failed to update organization status: ${error}`, 'Close');
      })
    ),
    { dispatch: false }
  );
}
